import { useCallback, useState } from 'react';
import { Env } from '../../shared';
import { useAdminApi, ApiError } from '../../shared/adminApi';

// PUT /admin/orgs/{orgId}/enterprise-price  body: { monthly_cents: <int|null> }
// Response shape matches backend/lambdas/orgs-admin/put-org-enterprise-price.

interface SetPriceResponse {
  orgId: string;
  enterpriseMonthlyCents: number | null;
}

export type SetPriceOutcome =
  | { ok: true; enterpriseMonthlyCents: number | null }
  | { ok: false; message: string };

interface UseSetEnterprisePriceResult {
  loading: boolean;
  /** cents === null clears the price (returns the org to not_yet_priced). */
  setPrice: (env: Env, orgId: string, cents: number | null) => Promise<SetPriceOutcome>;
}

export function useSetEnterprisePrice(): UseSetEnterprisePriceResult {
  const { request } = useAdminApi();
  const [loading, setLoading] = useState(false);

  const setPrice = useCallback(
    async (env: Env, orgId: string, cents: number | null): Promise<SetPriceOutcome> => {
      setLoading(true);
      try {
        const data = await request<SetPriceResponse>({
          env,
          path: `/admin/orgs/${orgId}/enterprise-price`,
          method: 'PUT',
          body: { monthly_cents: cents },
        });
        return { ok: true, enterpriseMonthlyCents: data.enterpriseMonthlyCents };
      } catch (err) {
        let message = err instanceof Error ? err.message : 'Failed to save price';
        if (err instanceof ApiError) {
          if (err.status === 404) message = 'No organization found with that ID.';
          else if (err.status === 400) message = 'The server rejected the amount. Enter a positive whole-dollar amount.';
        }
        return { ok: false, message };
      } finally {
        setLoading(false);
      }
    },
    [request],
  );

  return { loading, setPrice };
}
